import { useT } from "../../../shared/i18n/i18n";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { Placeholder } from "@tiptap/extensions";
import Mention from "@tiptap/extension-mention";
import StarterKit from "@tiptap/starter-kit";
import {
  ArrowUp,
  Bold,
  Code,
  CornerUpLeft,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Paperclip,
  Smile,
  Strikethrough,
  X,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { attachmentUrl, uploadAttachment } from "../../../shared/api/attachments";
import { cn } from "../../../shared/lib/cn";
import { EmojiPicker } from "../../../shared/ui/emoji-picker";
import { UserAvatar } from "../../../shared/ui/user-avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type MentionMember = { id: string; label: string };

export type ComposerAttachment = {
  contentType: string;
  downloadKey: string;
  fileName: string;
  id: string;
  sizeBytes: number;
};

type MentionState = {
  command: (attrs: { id: string; label: string }) => void;
  index: number;
  items: MentionMember[];
};

type CommentComposerProps = {
  autoFocus?: boolean;
  initialAttachments?: ComposerAttachment[];
  initialBody?: unknown;
  members: MentionMember[];
  onCancel?: () => void;
  onCancelReply?: () => void;
  onSubmit: (input: { attachmentIds: string[]; body: Record<string, unknown> }) => Promise<void>;
  placeholder?: string;
  replyTo?: { author: string; snippet: string } | null;
  submitLabel?: string;
  workObjectId: string;
};

const maxUploadBytes = 25 * 1024 * 1024;

export function CommentComposer({
  autoFocus = false,
  initialAttachments,
  initialBody,
  members,
  onCancel,
  onCancelReply,
  onSubmit,
  placeholder,
  replyTo = null,
  submitLabel,
  workObjectId,
}: CommentComposerProps) {
  const t = useT();
  const placeholderText = placeholder ?? t("item.writeComment");
  const [attachments, setAttachments] = useState<ComposerAttachment[]>(initialAttachments ?? []);
  const [pendingUploads, setPendingUploads] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mention, setMention] = useState<MentionState | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const membersRef = useRef(members);
  membersRef.current = members;
  const mentionRef = useRef(mention);
  mentionRef.current = mention;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const submitRef = useRef<() => void>(() => undefined);

  const mentionFloating = useFloating({
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    open: mention !== null && mention.items.length > 0,
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
  });
  const mentionRectRef = useRef<(() => DOMRect | null) | null>(null);
  const mentionFloatingRef = useRef(mentionFloating);
  mentionFloatingRef.current = mentionFloating;

  const mentionSuggestion = useMemo(
    () => ({
      char: "@",
      items: ({ query }: { query: string }) => filterMembers(membersRef.current, query),
      render: () => ({
        onStart(props: SuggestionRenderProps) {
          mentionRectRef.current = props.clientRect ?? null;
          mentionFloatingRef.current.refs.setReference({
            contextElement: props.editor?.view.dom,
            getBoundingClientRect: () => mentionRectRef.current?.() ?? new DOMRect(-9999, -9999, 0, 0),
          });
          setMention({ command: props.command, index: 0, items: props.items });
        },
        onUpdate(props: SuggestionRenderProps) {
          mentionRectRef.current = props.clientRect ?? null;
          setMention((current) => ({
            command: props.command,
            index: Math.min(current?.index ?? 0, Math.max(props.items.length - 1, 0)),
            items: props.items,
          }));
          mentionFloatingRef.current.update();
        },
        onKeyDown({ event }: { event: KeyboardEvent }) {
          const state = mentionRef.current;
          if (!state) return false;
          if (event.key === "Escape") {
            setMention(null);
            return true;
          }
          if (state.items.length === 0) return false;
          if (event.key === "ArrowDown") {
            setMention((current) => (current ? { ...current, index: (current.index + 1) % current.items.length } : current));
            return true;
          }
          if (event.key === "ArrowUp") {
            setMention((current) =>
              current ? { ...current, index: (current.index - 1 + current.items.length) % current.items.length } : current,
            );
            return true;
          }
          if (event.key === "Enter" || event.key === "Tab") {
            const item = state.items[state.index];
            if (item) state.command({ id: item.id, label: item.label });
            return true;
          }
          return false;
        },
        onExit() {
          setMention(null);
        },
      }),
    }),
    [],
  );

  const editor = useEditor(
    {
      autofocus: autoFocus ? "end" : false,
      content: (initialBody as never) ?? "",
      editorProps: {
        attributes: {
          class: "comment-editor max-h-56 min-h-[3.25rem] overflow-y-auto px-3 py-2.5 text-sm focus:outline-none",
        },
        handleDrop: (_view, event, _slice, moved) => {
          if (moved || !event.dataTransfer?.files.length) return false;
          void addFiles(event.dataTransfer.files);
          return true;
        },
        handleKeyDown: (view, event) => {
          if (event.key === "Escape" && !mentionRef.current && onCancelRef.current) {
            event.stopPropagation();
            onCancelRef.current();
            return true;
          }
          if (event.key !== "Enter" || event.altKey) return false;
          if (mentionRef.current && mentionRef.current.items.length > 0) return false;

          let inCodeBlock = false;
          let inListItem = false;
          const { $from } = view.state.selection;
          for (let depth = $from.depth; depth > 0; depth -= 1) {
            const name = $from.node(depth).type.name;
            if (name === "codeBlock") inCodeBlock = true;
            if (name === "listItem") inListItem = true;
          }

          if (event.shiftKey) {
            if (!event.metaKey && !event.ctrlKey) return false;
            const hardBreak = view.state.schema.nodes.hardBreak;
            const tr =
              inCodeBlock || !hardBreak
                ? view.state.tr.insertText("\n")
                : view.state.tr.replaceSelectionWith(hardBreak.create());
            view.dispatch(tr.scrollIntoView());
            return true;
          }
          if (!event.metaKey && !event.ctrlKey && (inCodeBlock || inListItem)) return false;
          submitRef.current();
          return true;
        },
        handlePaste: (_view, event) => {
          const files = event.clipboardData?.files;
          if (!files || files.length === 0) return false;
          void addFiles(files);
          return true;
        },
      },
      extensions: [
        StarterKit.configure({
          heading: false,
          horizontalRule: false,
          link: { autolink: true, openOnClick: false },
          underline: false,
        }),
        Placeholder.configure({ placeholder: placeholderText }),
        Mention.configure({
          HTMLAttributes: { class: "comment-mention" },
          deleteTriggerWithBackspace: true,
          renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
          suggestion: mentionSuggestion,
        }),
      ],
    },
    [],
  );

  const editorState = useEditorState({
    editor,
    selector: (context) => ({
      isBold: context.editor?.isActive("bold") ?? false,
      isBulletList: context.editor?.isActive("bulletList") ?? false,
      isCode: context.editor?.isActive("code") ?? false,
      isEmpty: context.editor?.isEmpty ?? true,
      isItalic: context.editor?.isActive("italic") ?? false,
      isOrderedList: context.editor?.isActive("orderedList") ?? false,
      isStrike: context.editor?.isActive("strike") ?? false,
    }),
  });

  async function addFiles(files: FileList | File[]) {
    setError(null);
    for (const file of Array.from(files)) {
      if (file.size > maxUploadBytes) {
        setError(`${file.name} is larger than 25 MB.`);
        continue;
      }
      const uploadKey = `${file.name}:${Date.now()}:${Math.random()}`;
      setPendingUploads((current) => [...current, uploadKey]);
      try {
        const uploaded = await uploadAttachment(file);
        setAttachments((current) => [
          ...current,
          {
            contentType: uploaded.contentType,
            downloadKey: uploaded.downloadKey,
            fileName: uploaded.fileName,
            id: uploaded.id,
            sizeBytes: uploaded.sizeBytes,
          },
        ]);
      } catch (uploadError) {
        setError(uploadError instanceof Error ? uploadError.message : "Could not upload the file.");
      } finally {
        setPendingUploads((current) => current.filter((key) => key !== uploadKey));
      }
    }
  }

  async function submit() {
    if (!editor || isSubmitting || pendingUploads.length > 0) return;
    const text = editor.getText().trim();
    if (text.length === 0 && attachments.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        attachmentIds: attachments.map((attachment) => attachment.id),
        body: editor.getJSON() as Record<string, unknown>,
      });
      editor.commands.clearContent(true);
      setAttachments([]);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not send the comment.");
    } finally {
      setIsSubmitting(false);
    }
  }
  submitRef.current = () => void submit();

  if (!editor) return null;

  const canSubmit = (!editorState.isEmpty || attachments.length > 0) && pendingUploads.length === 0 && !isSubmitting;

  return (
    <div className="flex flex-col gap-2">
      {replyTo ? (
        <div className="flex items-start gap-2 rounded-md bg-muted/40 px-2.5 py-1.5 text-xs">
          <CornerUpLeft className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <span className="font-medium">Replying to {replyTo.author}</span>
            <p className="truncate text-muted-foreground">{replyTo.snippet}</p>
          </div>
          <Button aria-label={t("item.comments.cancelReply")} className="size-5 shrink-0" onClick={onCancelReply} size="icon-sm" variant="ghost">
            <X className="size-3.5" />
          </Button>
        </div>
      ) : null}

      <div className="rounded-lg border border-border/70 bg-background shadow-xs transition-shadow focus-within:border-border focus-within:shadow-sm">
        <EditorContent editor={editor} />

        {attachments.length > 0 || pendingUploads.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 px-3 pb-1">
            {attachments.map((attachment) => (
              <AttachmentChip
                attachment={attachment}
                key={attachment.id}
                onRemove={() => setAttachments((current) => current.filter((entry) => entry.id !== attachment.id))}
              />
            ))}
            {pendingUploads.map((key) => (
              <span className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground" key={key}>
                <Loader2 className="size-3.5 animate-spin" /> {t("item.comments.uploading")}
              </span>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-0.5 px-1.5 pb-1.5">
          <ToolbarButton active={editorState.isBold} label={t("item.comments.bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold />
          </ToolbarButton>
          <ToolbarButton active={editorState.isItalic} label={t("item.comments.italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic />
          </ToolbarButton>
          <ToolbarButton active={editorState.isStrike} label={t("item.comments.strikethrough")} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Strikethrough />
          </ToolbarButton>
          <ToolbarButton active={editorState.isCode} label={t("item.comments.code")} onClick={() => editor.chain().focus().toggleCode().run()}>
            <Code />
          </ToolbarButton>
          <ToolbarButton active={editorState.isBulletList} label={t("item.comments.bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <List />
          </ToolbarButton>
          <ToolbarButton
            active={editorState.isOrderedList}
            label={t("item.comments.numberedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered />
          </ToolbarButton>

          <div className="mx-0.5 h-4 w-px bg-border/70" />

          <ToolbarButton label={t("item.comments.attach")} onClick={() => fileInputRef.current?.click()}>
            <Paperclip />
          </ToolbarButton>
          <Popover>
            <PopoverTrigger
              render={
                <Button aria-label={t("item.comments.emoji")} className="size-7 text-muted-foreground" size="icon-sm" type="button" variant="ghost">
                  <Smile />
                </Button>
              }
            />
            <PopoverContent align="start" className="w-auto p-0">
              <EmojiPicker onPick={(emoji) => editor.chain().focus().insertContent(emoji).run()} />
            </PopoverContent>
          </Popover>

          <div className="ml-auto flex items-center gap-1.5">
            {onCancel ? (
              <Button
                aria-label={t("common.cancel")}
                className="size-7 text-muted-foreground"
                onClick={onCancel}
                size="icon-sm"
                title={t("item.comments.cancelEdit")}
                type="button"
                variant="ghost"
              >
                <X className="size-4" />
              </Button>
            ) : null}
            <Button
              aria-label={submitLabel ?? t("common.send")}
              className="size-7 rounded-full"
              disabled={!canSubmit}
              onClick={() => void submit()}
              size="icon-sm"
              type="button"
            >
              {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
            </Button>
          </div>
        </div>
      </div>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <input
        className="hidden"
        multiple
        onChange={(event) => {
          if (event.target.files) void addFiles(event.target.files);
          event.target.value = "";
        }}
        ref={fileInputRef}
        type="file"
      />

      {mention && mention.items.length > 0
        ? createPortal(
            <div
              className="z-[100] flex w-60 flex-col gap-0.5 rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
              ref={mentionFloating.refs.setFloating}
              style={{ ...mentionFloating.floatingStyles, visibility: mentionFloating.isPositioned ? "visible" : "hidden" }}
            >
              {mention.items.map((item, index) => (
                <button
                  className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                    index === mention.index ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                  )}
                  key={item.id}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    mention.command({ id: item.id, label: item.label });
                  }}
                  onMouseEnter={() => setMention((current) => (current ? { ...current, index } : current))}
                  type="button"
                >
                  <UserAvatar className="size-6 text-[0.6rem]" name={item.label} seed={item.id} />
                  <span className="min-w-0 truncate">{item.label}</span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

type SuggestionRenderProps = {
  clientRect?: (() => DOMRect | null) | null;
  command: (attrs: { id: string; label: string }) => void;
  editor?: { view: { dom: HTMLElement } };
  items: MentionMember[];
};

function filterMembers(members: MentionMember[], query: string): MentionMember[] {
  const normalized = query.trim().toLowerCase();
  const matches = normalized
    ? members.filter((member) => member.label.toLowerCase().includes(normalized))
    : members;
  return matches.slice(0, 8);
}

function ToolbarButton({
  active = false,
  children,
  label,
  onClick,
}: {
  active?: boolean;
  children: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={label}
      aria-pressed={active}
      className={cn("size-7 text-muted-foreground", active && "bg-accent text-accent-foreground")}
      onClick={onClick}
      size="icon-sm"
      type="button"
      variant="ghost"
    >
      {children}
    </Button>
  );
}

function AttachmentChip({ attachment, onRemove }: { attachment: ComposerAttachment; onRemove: () => void }) {
  const t = useT();
  const isImage = attachment.contentType.startsWith("image/");
  return (
    <span className="group/chip relative flex items-center gap-1.5 rounded-md bg-muted/50 py-1 pl-1.5 pr-6 text-xs">
      {isImage ? (
        <img
          alt={attachment.fileName}
          className="size-6 rounded object-cover"
          src={attachmentUrl(attachment)}
        />
      ) : (
        <Paperclip className="size-3.5 text-muted-foreground" />
      )}
      <span className="max-w-40 truncate">{attachment.fileName}</span>
      <span className="text-muted-foreground">{formatFileSize(attachment.sizeBytes)}</span>
      <button
        aria-label={t("a11y.remove", { name: attachment.fileName })}
        className="absolute right-1 top-1/2 grid size-4 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        onClick={onRemove}
        type="button"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

export function formatFileSize(sizeBytes: number) {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
